import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, trackEvent } from '../firebase';
import AuthLayout from '../components/AuthLayout';

/** Map Firebase Auth error codes to user-friendly messages. */
function getAuthErrorMessage(err: unknown): string {
  const code = (err as { code?: string }).code ?? '';
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Please sign in instead.';

    case 'auth/invalid-email':
      return 'Invalid email address. Please check and try again.';

    case 'auth/weak-password':
      return 'Password does not meet the requirements listed below.';

    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment before trying again.';

    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';

    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return '';

    case 'auth/popup-blocked':
      return 'Pop-up was blocked by your browser. Please allow pop-ups for this site.';

    default:
      return 'Something went wrong. Please try again.';
  }
}

interface PasswordChecks {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

function checkPassword(pw: string): PasswordChecks {
  return {
    length: pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
}

function isStrongPassword(checks: PasswordChecks): boolean {
  return checks.length && checks.uppercase && checks.lowercase && checks.number && checks.special;
}

function Requirement({ met, label }: { met: boolean; label: string }) {
  return (
    <li className={`flex items-center gap-1.5 ${met ? 'text-green-600' : 'text-gray-400'}`}>
      <span className="text-xs leading-none">{met ? '✓' : '○'}</span>
      <span>{label}</span>
    </li>
  );
}

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const pwChecks = checkPassword(password);
  const showRequirements = password.length > 0;

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isStrongPassword(pwChecks)) {
      setError('Please make sure your password meets all the requirements below.');
      return;
    }

    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      trackEvent('signup_email');
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const msg = getAuthErrorMessage(err);
      if (msg) {
        trackEvent('signup_failed', { method: 'email' });
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      trackEvent('signup_google');
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const msg = getAuthErrorMessage(err);
      if (msg) {
        trackEvent('signup_failed', { method: 'google' });
        setError(msg);
      }
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-sm mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Create your account</h1>
        <p className="text-sm text-gray-500 mb-8">
          Every launch deserves an audience. Your first project is free.
        </p>

        <form onSubmit={handleEmailSignUp} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
            />
          </label>

          {showRequirements && (
            <ul className="text-xs flex flex-col gap-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 list-none m-0 p-0">
              <Requirement met={pwChecks.length} label="At least 8 characters" />
              <Requirement met={pwChecks.uppercase} label="One uppercase letter (A–Z)" />
              <Requirement met={pwChecks.lowercase} label="One lowercase letter (a–z)" />
              <Requirement met={pwChecks.number} label="One number (0–9)" />
              <Requirement met={pwChecks.special} label="One special character (!@#$…)" />
            </ul>
          )}

          {error && <p className="text-red-500 text-sm m-0">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 text-white border-0 rounded-full text-base font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-1"
          >
            {loading ? 'Please wait…' : 'Get started free'}
          </button>
        </form>

        <div className="relative text-center text-sm text-gray-400 my-5">
          <span className="relative z-10 bg-white px-3">or</span>
          <span className="absolute inset-y-1/2 left-0 right-0 h-px bg-gray-200 -translate-y-1/2" />
        </div>

        <button
          onClick={handleGoogle}
          className="w-full py-2.5 bg-white text-gray-700 border border-gray-300 rounded-full text-base font-semibold hover:bg-gray-50 transition-colors"
        >
          Continue with Google
        </button>

        <p className="text-center text-sm text-gray-500 mt-6 mb-0">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-500 font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
